import React, { useState, useEffect } from 'react';
import { getPricingInfo } from '../../services/api';
import { trackEvent } from '../../utils/analytics';
import './Pricing.css';

const Pricing = () => {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page_title: 'Pricing',
      page_location: window.location.href,
      page_path: '/pricing',
    });

    fetchPricingInfo();
  }, []);

  const fetchPricingInfo = async () => {
    try {
      // In a real implementation, we would fetch from the API
      // For now, we'll use sample data that matches the spec
      const sampleData = {
        consumer: {
          name: "Consumer Plan",
          description: "Perfect for individual EV owners",
          price: 0,
          currency: "USD",
          billingCycle: "monthly",
          features: [
            "Find and reserve charging stations",
            "Access to network of charging stations",
            "Basic charging analytics",
            "Mobile app access"
          ],
          trialAvailable: true,
          trialDays: 7
        },
        fleet: {
          name: "Fleet Plan",
          description: "For businesses with multiple EVs",
          price: 49,
          currency: "USD",
          billingCycle: "per-site-per-month",
          features: [
            "Site management tools",
            "Advanced scheduling",
            "Detailed reporting",
            "Priority support",
            "Custom branding"
          ],
          trialAvailable: true,
          trialDays: 7
        }
      };
      
      setPricingData(sampleData);
    } catch (err) {
      setError('Failed to load pricing information. Please try again later.');
      console.error('Error fetching pricing:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pricing-page">
        <section className="hero">
          <div className="container">
            <h1>Pricing Plans</h1>
            <p>Loading pricing information...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pricing-page">
        <section className="hero">
          <div className="container">
            <h1>Pricing Plans</h1>
            <p>{error}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <section className="hero">
        <div className="container">
          <h1>Pricing Plans</h1>
          <p>Simple, transparent pricing with a 7-day free trial for each plan.</p>
        </div>
      </section>

      <section className="pricing-content">
        <div className="container">
          <div className="pricing-plans">
            <div className="pricing-plan consumer">
              <div className="plan-header">
                <h2>{pricingData.consumer.name}</h2>
                <p>{pricingData.consumer.description}</p>
                <div className="price">
                  <span className="amount">${pricingData.consumer.price}</span>
                  <span className="cycle">/ {pricingData.consumer.billingCycle}</span>
                </div>
                {pricingData.consumer.trialAvailable && (
                  <div className="trial-badge">
                    {pricingData.consumer.trialDays}-Day Free Trial
                  </div>
                )}
              </div>
              
              <ul className="features">
                {pricingData.consumer.features.map((feature, index) => (
                  <li key={index} className="feature">
                    <span className="icon">✓</span> {feature}
                  </li>
                ))}
              </ul>
              
              <div className="plan-action">
                <a 
                  href="/signup" 
                  className="btn-primary"
                  onClick={() => trackEvent('pricing_cta_click', { plan_type: 'consumer' })}
                >
                  Start Free Trial
                </a>
              </div>
            </div>
            
            <div className="pricing-plan fleet featured">
              <div className="plan-header">
                <h2>{pricingData.fleet.name}</h2>
                <p>{pricingData.fleet.description}</p>
                <div className="price">
                  <span className="amount">${pricingData.fleet.price}</span>
                  <span className="cycle">/ per site/ month</span>
                </div>
                {pricingData.fleet.trialAvailable && (
                  <div className="trial-badge">
                    {pricingData.fleet.trialDays}-Day Free Trial
                  </div>
                )}
              </div>
              
              <ul className="features">
                {pricingData.fleet.features.map((feature, index) => (
                  <li key={index} className="feature">
                    <span className="icon">✓</span> {feature}
                  </li>
                ))}
              </ul>
              
              <div className="plan-action">
                <a 
                  href="/signup" 
                  className="btn-primary"
                  onClick={() => trackEvent('pricing_cta_click', { plan_type: 'fleet' })}
                >
                  Start Free Trial
                </a>
              </div>
            </div>
          </div>
          
          <div className="pricing-faq">
            <h3>Frequently Asked Questions</h3>
            <div className="faq-item">
              <h4>What payment methods do you accept?</h4>
              <p>We accept all major credit cards including Visa, Mastercard, and American Express.</p>
            </div>
            <div className="faq-item">
              <h4>Can I change plans later?</h4>
              <p>Yes, you can upgrade, downgrade, or cancel your plan at any time.</p>
            </div>
            <div className="faq-item">
              <h4>Is there a setup fee?</h4>
              <p>No, there are no setup fees for any of our plans.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;